"use client";

import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { useEffect } from "react";

/**
 * Hook to access modal data from the global modal stack
 *
 * @template T - The type of data stored in the modal
 * @param modalId - The ID of the modal to get data for
 * @returns The data stored in the modal, or undefined if no data
 *
 * @example
 * // In a modal component
 * const modalData = useModalContext<TaskCreationModalData>("create-tasks");
 * const { contactId, ticketId, prefillData } = modalData || {};
 */
export function useModalContext<T = unknown>(modalId: string): T | undefined {
  const { getModalData } = useGlobalModalStack();
  const data = getModalData(modalId) as T | undefined;

  // Debug logging
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      if (data) {
        console.group(`🎯 Modal Context: ${modalId}`);
        console.log("Data received:", data);
        console.log("Data keys:", Object.keys(data));
        console.groupEnd();
      } else {
        console.warn(`⚠️ Modal Context: ${modalId} - No data received`);
      }
    }
  }, [data, modalId]);

  return data;
}
